import type {
  LHCDetails,
  LhcFilter,
  LhcItemsFilter,
  LhcItemsResponse,
  LhcListResponse,
} from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';

import type {
  CollectionPlantsResponse,
  CreateLHCPayload,
  LHCDetailInvoiceRowData,
  LHCRowData,
  UpdateLHCPayload,
} from '../types';
import { COLLECTION_API_ENDPOINTS } from './constants';
import { mapInvoiceItemsToRows, mapLHCItemToRowData } from './mappers';

export { mapInvoiceItemsToRows } from './mappers';

/**
 * Maps LHC items API response to flat table rows (parent + child).
 * Use this when you have LhcItemsResponse from getLHCDetail.
 */
export function getLHCItemsAsRows(
  response: LhcItemsResponse
): LHCDetailInvoiceRowData[] {
  const plantName = response.lhc.plant_name || '';
  return mapInvoiceItemsToRows(response.data, plantName);
}

export interface LHCListResult {
  data: LHCRowData[];
  pagination: {
    rows: number;
    pages: number;
  };
  total: number;
  totalItems: number;
}

export async function getLHCList(
  token: string,
  params: LhcFilter
): Promise<LHCListResult> {
  const response = await apiInstance(token).get<LhcListResponse>(
    COLLECTION_API_ENDPOINTS.LHC_LIST,
    { params }
  );

  const pageOffset = (params.page - 1) * params.size;

  return {
    data: response.data.data.map((item, index) =>
      mapLHCItemToRowData(item as LHCDetails, index, pageOffset)
    ),
    pagination: response.data.pagination,
    total: response.data.total,
    totalItems: response.data.total_items,
  };
}

export async function getCollectionPlants(
  token: string,
  searchQuery?: string
): Promise<CollectionPlantsResponse> {
  const response = await apiInstance(token).get<CollectionPlantsResponse>(
    COLLECTION_API_ENDPOINTS.COLLECTION_PLANTS,
    { params: searchQuery ? { search_query: searchQuery } : undefined }
  );
  return response.data;
}

export async function getLHCDetail(
  token: string,
  id: string,
  params?: LhcItemsFilter
): Promise<LhcItemsResponse> {
  const response = await apiInstance(token).get<LhcItemsResponse>(
    COLLECTION_API_ENDPOINTS.LHC_DETAIL(id),
    { params }
  );
  return response.data;
}

export async function createLHC(
  token: string,
  payload: CreateLHCPayload
): Promise<LHCDetails> {
  const response = await apiInstance(token).post<LHCDetails>(
    COLLECTION_API_ENDPOINTS.CREATE_LHC,
    payload
  );
  return response.data;
}

export async function updateLHC(
  token: string,
  id: string,
  payload: UpdateLHCPayload
): Promise<LHCDetails> {
  const response = await apiInstance(token).patch<LHCDetails>(
    COLLECTION_API_ENDPOINTS.UPDATE_LHC(id),
    payload
  );
  return response.data;
}

export interface ExportLHCResponse {
  data: Blob;
  filename: string;
}

export async function exportLHC(
  token: string,
  id: string
): Promise<ExportLHCResponse> {
  const response = await apiInstance(token).get(
    COLLECTION_API_ENDPOINTS.LHC_EXPORT(id),
    {
      responseType: 'blob',
    }
  );

  const filename =
    response.headers['content-disposition']?.split('filename=')[1] ||
    `lhc-${id}.xlsx`;

  return {
    data: response.data,
    filename,
  };
}
